import { getLegacyQueueRescueLevel, isAwaitingAgentReply } from './legacyQueue';

export const PINNED_CONVERSATION_IDS_KEY = 'pinned_conversation_ids';
export const QUIET_CONVERSATION_IDS_KEY = 'quiet_conversation_ids';

export const isTeamQuietConversation = conversation => {
  if (Object.prototype.hasOwnProperty.call(conversation || {}, 'team_quiet')) {
    return conversation.team_quiet === true;
  }
  if (Object.prototype.hasOwnProperty.call(conversation || {}, 'teamQuiet')) {
    return conversation.teamQuiet === true;
  }

  return (
    conversation?.additional_attributes?.team_quiet === true ||
    conversation?.additionalAttributes?.team_quiet === true
  );
};

const normalizeConversationId = conversationId => {
  const normalizedId = Number(conversationId);
  return Number.isInteger(normalizedId) && normalizedId > 0
    ? normalizedId
    : null;
};

export const normalizeConversationIds = conversationIds => {
  if (!Array.isArray(conversationIds)) return [];

  return [
    ...new Set(conversationIds.map(normalizeConversationId).filter(Boolean)),
  ];
};

export const getPersonalConversationIds = (uiSettings, key) =>
  normalizeConversationIds(uiSettings?.[key]);

export const hasPersonalConversationPreference = (
  uiSettings,
  key,
  conversationId
) => {
  const normalizedId = normalizeConversationId(conversationId);
  if (!normalizedId) return false;

  return getPersonalConversationIds(uiSettings, key).includes(normalizedId);
};

export const togglePersonalConversationPreference = (
  uiSettings,
  key,
  conversationId
) => {
  const normalizedId = normalizeConversationId(conversationId);
  const currentIds = getPersonalConversationIds(uiSettings, key);
  if (!normalizedId) return currentIds;

  if (currentIds.includes(normalizedId)) {
    return currentIds.filter(id => id !== normalizedId);
  }

  return [normalizedId, ...currentIds];
};

export const resolvePinnedConversations = (uiSettings, conversations) => {
  const conversationsById = new Map(
    conversations.map(conversation => [conversation.id, conversation])
  );

  return getPersonalConversationIds(
    uiSettings,
    PINNED_CONVERSATION_IDS_KEY
  )
    .map(id => conversationsById.get(id))
    .filter(Boolean);
};

const preferenceRank = (conversation, uiSettings) => {
  const rescueLevel = getLegacyQueueRescueLevel(conversation);
  if (rescueLevel > 0) return rescueLevel === 2 ? 0 : 1;

  if (
    hasPersonalConversationPreference(
      uiSettings,
      PINNED_CONVERSATION_IDS_KEY,
      conversation.id
    )
  ) {
    return 2;
  }

  if (
    hasPersonalConversationPreference(
      uiSettings,
      QUIET_CONVERSATION_IDS_KEY,
      conversation.id
    )
  ) {
    return 5;
  }

  if (isTeamQuietConversation(conversation)) return 6;

  // Reading a conversation must not make an unanswered customer disappear
  // behind already-answered conversations.
  if (isAwaitingAgentReply(conversation)) return 3;

  return 4;
};

export const sortConversationsByPersonalPreferences = (
  conversations,
  uiSettings
) => {
  const pinnedOrder = new Map(
    getPersonalConversationIds(uiSettings, PINNED_CONVERSATION_IDS_KEY).map(
      (id, index) => [id, index]
    )
  );
  const quietOrder = new Map(
    getPersonalConversationIds(uiSettings, QUIET_CONVERSATION_IDS_KEY).map(
      (id, index) => [id, index]
    )
  );

  return conversations
    .map((conversation, originalIndex) => ({
      conversation,
      originalIndex,
      rank: preferenceRank(conversation, uiSettings),
    }))
    .sort((left, right) => {
      if (left.rank !== right.rank) return left.rank - right.rank;

      if (left.rank === 2) {
        return (
          (pinnedOrder.get(left.conversation.id) ?? Number.MAX_SAFE_INTEGER) -
          (pinnedOrder.get(right.conversation.id) ?? Number.MAX_SAFE_INTEGER)
        );
      }

      if (left.rank === 5) {
        return (
          (quietOrder.get(left.conversation.id) ?? Number.MAX_SAFE_INTEGER) -
          (quietOrder.get(right.conversation.id) ?? Number.MAX_SAFE_INTEGER)
        );
      }

      if (left.rank === 6) {
        return left.conversation.id - right.conversation.id;
      }

      return left.originalIndex - right.originalIndex;
    })
    .map(({ conversation }) => conversation);
};
